import { render } from 'preact';
import { useEffect, useLayoutEffect, useReducer, useRef, useState } from 'preact/hooks';
import { Editor } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { UndoRedo, Dropcursor, Gapcursor } from '@tiptap/extensions';
import './style.css';
import { clean } from './schema.js';
import { captionsForEditing, captionsForSaving } from './captions.js';
import { extensions, setMediaSlug, setMediaInUse, noteEditor, mediaUrl, MediaPicker, Form, empty } from './views.jsx';
import Document from '@tiptap/extension-document';
import { Icon, IconButton } from './icons.jsx';

const p = () => ({ type: 'paragraph' });
const table = (rows, cols) => ({
  type: 'table',
  content: Array.from({ length: rows }, (_, r) => ({
    type: 'tableRow',
    content: Array.from({ length: cols }, () => ({ type: r ? 'tableCell' : 'tableHeader', content: [p()] })),
  })),
});
const figure = (media) => ({ type: 'figure', content: [media, { type: 'caption' }] });
const youtube = { type: 'embed', attrs: { provider: 'youtube', type: 'video' } };

// The Blocks tab. Media come in a figure, so they always have a caption line.
const BLOCKS = [
  { label: 'Image', icon: 'image', json: figure({ type: 'image' }) },
  { label: 'Gallery', icon: 'gallery', json: { type: 'gallery', content: [{ type: 'image' }, { type: 'caption' }] } },
  { label: 'Video', icon: 'video', json: figure({ type: 'video' }) },
  { label: 'YouTube', icon: 'youtube', json: figure(youtube) },
  { label: 'Table', icon: 'table', json: figure(table(3, 2)) },
  { label: 'Tweet', icon: 'tweet', json: { type: 'tweet', content: [{ type: 'blockquote', content: [p()] }] } },
  { label: 'Accordion', icon: 'details', json: { type: 'details', content: [{ type: 'summary' }, p()] } },
  { label: 'Math', icon: 'math', json: { type: 'mathBlock' } },
  { label: 'HTML', icon: 'html', json: { type: 'rawHtml' } },
];

// Blocks go after the selected node or the paragraph holding the cursor, or
// replace that paragraph when it is empty; they never split text or a block
// that cannot hold them (they go after it instead). The new block opens for
// editing: its first atom is selected, or the cursor goes into its first text.
function insertBlock(editor, json) {
  const { state } = editor;
  const node = state.schema.nodeFromJSON(json);
  const { selection, doc } = state;
  const { $from } = selection;
  let tr = null;
  let at;
  if (
    !(selection instanceof NodeSelection) &&
    $from.parent.isTextblock &&
    $from.parent.content.size === 0 &&
    $from.node(-1).canReplaceWith($from.index(-1), $from.index(-1) + 1, node.type)
  ) {
    at = $from.before();
    tr = state.tr.replaceWith(at, $from.after(), node);
  } else {
    let $p = doc.resolve(
      selection instanceof NodeSelection ? selection.to : $from.parent.isTextblock ? $from.after() : $from.pos
    );
    while (!$p.parent.canReplaceWith($p.index(), $p.index(), node.type)) {
      if ($p.depth === 0) return;
      $p = doc.resolve($p.after());
    }
    at = $p.pos;
    tr = state.tr.insert(at, node);
  }
  let sel = null;
  tr.doc.nodesBetween(at, at + node.nodeSize, (n, pos) => {
    if (sel || pos < at) return !sel;
    if (n.isAtom && n.isBlock) sel = NodeSelection.create(tr.doc, pos);
    else if (n.isTextblock) sel = TextSelection.create(tr.doc, pos + 1);
    return !sel;
  });
  if (sel) tr.setSelection(sel);
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
}

// Insert an inline atom (footnote, math) and select it, which opens its popup.
function insertInline(ed, json) {
  const pos = ed.state.selection.from;
  ed.chain().focus().insertContent(json).setNodeSelection(pos).run();
}

function Toolbar({ editor, onLink, captionEditor }) {
  const [tab, setTab] = useState('format');
  // Inline buttons act on the footnote editor while it has focus.
  const target = () => [noteEditor, captionEditor].find((e) => e?.isFocused) || editor;
  const is = (n, a) => target().isActive(n, a);
  const btn = (label, title, active, run, cls = '') => (
    <IconButton class={cls} title={title} active={active} run={run}>
      {label}
    </IconButton>
  );
  const c = () => editor.chain().focus();
  const t = () => target().chain().focus();
  return (
    <div class="toolbar">
      <div class="tabs" role="tablist">
        {[
          ['format', 'Format'],
          ['blocks', 'Blocks'],
        ].map(([k, l]) => (
          <button
            role="tab"
            aria-selected={tab === k}
            class={'tab' + (tab === k ? ' on' : '')}
            onMouseDown={(e) => (e.preventDefault(), setTab(k))}
          >
            {l}
          </button>
        ))}
      </div>
      {tab === 'format' ? (
        <div class="tools">
          <span class="group">
            {btn('¶', 'Paragraph', is('paragraph') && !is('heading'), () => c().setParagraph().run())}
            {[2, 3, 4, 5].map((l) =>
              btn('H' + l, 'Heading ' + l, is('heading', { level: l }), () => c().setHeading({ level: l }).run())
            )}
          </span>
          <span class="group">
            {btn(<Icon name="bullet" />, 'Bulleted list', is('bulletList'), () => c().toggleBulletList().run())}
            {btn(<Icon name="ordered" />, 'Numbered list', is('orderedList'), () => c().toggleOrderedList().run())}
            {btn(<Icon name="quote" />, 'Quote', is('blockquote'), () => c().toggleBlockquote().run())}
          </span>
          <span class="group">
            {btn(<b>B</b>, 'Bold', is('bold'), () => t().toggleBold().run())}
            {btn(<i>I</i>, 'Italic', is('italic'), () => t().toggleItalic().run())}
            {btn(
              <span>
                x<sup>2</sup>
              </span>,
              'Superscript',
              is('superscript'),
              () => t().toggleSuperscript().run()
            )}
            {btn(
              <span>
                x<sub>2</sub>
              </span>,
              'Subscript',
              is('subscript'),
              () => t().toggleSubscript().run()
            )}
          </span>
          <span class="group">
            {btn(<Icon name="link" />, 'Link (select text first)', is('link'), () => {
              const ed = target();
              if (!ed.isActive('link')) {
                if (ed.state.selection.empty) return;
                ed.chain().focus().setLink({ href: 'https://' }).run();
              }
              onLink();
            })}
            {btn(<Icon name="footnote" />, 'Footnote', false, () => insertInline(editor, { type: 'footnote' }))}
            {btn(<i>f(x)</i>, 'Inline math', false, () => insertInline(target(), { type: 'mathInline' }))}
          </span>
        </div>
      ) : (
        <div class="tools">
          {BLOCKS.map((b) =>
            btn(
              <>
                <Icon name={b.icon} />
                <span>{b.label}</span>
              </>,
              b.label,
              false,
              () => insertBlock(editor, b.json),
              'block-btn'
            )
          )}
        </div>
      )}
    </div>
  );
}

// The featured image caption: one line of text with marks, edited in place.
function CaptionEditor({ content, onChange, onEditor }) {
  const ref = useRef();
  useEffect(() => {
    const ed = new Editor({
      element: ref.current,
      extensions: [
        Document.extend({
          content: 'paragraph',
          addKeyboardShortcuts: () => ({ Enter: ({ editor: e }) => e.commands.setHardBreak() }),
        }),
        ...extensions.filter((e) =>
          ['text', 'paragraph', 'bold', 'italic', 'link', 'superscript', 'subscript', 'hardBreak'].includes(e.name)
        ),
        UndoRedo,
      ],
      content: { type: 'doc', content: [{ type: 'paragraph', ...(content?.length ? { content } : {}) }] },
      onUpdate: ({ editor: e }) => onChange(clean(e.getJSON(), e.schema).content[0].content || []),
      onTransaction: ({ editor: e }) => onEditor(e),
    });
    onEditor(ed);
    return () => ed.destroy();
  }, []);
  return <div class="featured-caption" ref={ref} />;
}

// The featured image, under the title: always there, never inserted. It edits
// featured_image, featured_alt and featured_caption, not the body.
function FeaturedImage({ post, change, onCaptionEditor }) {
  const [editing, setEditing] = useState(false);
  const [captionEd, setCaptionEd] = useState(null);
  const ref = useRef();
  const host = useRef();
  useEffect(() => {
    const down = (e) => !ref.current?.contains(e.target) && !e.target.closest?.('.toolbar') && setEditing(false);
    document.addEventListener('click', down, true);
    return () => document.removeEventListener('click', down, true);
  }, []);
  const a = { src: post.featured_image, alt: post.featured_alt };
  const set = (attrs) => {
    if ('src' in attrs) change({ featured_image: attrs.src || null });
    if ('alt' in attrs) change({ featured_alt: attrs.alt || undefined });
  };
  return (
    <div class="featured">
      <div class="featured-pic" ref={ref}>
        <div class={'featured-main' + (editing ? ' editing' : '')} onMouseDown={() => setEditing(true)}>
          {editing && a.src && (
            <IconButton class="trash" icon="trash" title="Remove the featured image" run={() => set({ src: null })} />
          )}
          <span class="featured-label">Featured image</span>
          {a.src ? (
            <img src={mediaUrl(a.src)} alt={a.alt || ''} referrerpolicy="no-referrer" />
          ) : (
            empty('No featured image. Click to pick one.')
          )}
        </div>
        {editing && (
          <div class="edit">
            <MediaPicker kind="image" value={a.src} onPick={(src) => set({ src })} />
            <Form attrs={a} set={set} rows={[[['alt', 'text', 'Alt text', 'Describe the picture']]]} />
          </div>
        )}
      </div>
      <div class="caption-host" ref={host}>
        <CaptionEditor
          content={post.featured_caption}
          onChange={(c) => change({ featured_caption: c.length ? c : undefined })}
          onEditor={(e) => (setCaptionEd(e), onCaptionEditor(e))}
        />
        {captionEd && <LinkPopup editor={captionEd} host={host.current} focus={{ current: false }} />}
      </div>
    </div>
  );
}

// A popup under the link holding the cursor: edit the URL, open it, or remove the link.
function LinkPopup({ editor, host, focus }) {
  const input = useRef();
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const show = () => setDismissed(false);
    const hide = (e) => !e.target.closest?.('.link-popup, .ProseMirror, .toolbar') && setDismissed(true);
    editor.on('selectionUpdate', show);
    document.addEventListener('mousedown', hide, true);
    return () => {
      editor.off('selectionUpdate', show);
      document.removeEventListener('mousedown', hide, true);
    };
  }, [editor]);
  const active = editor.isActive('link') && !dismissed && host;
  useEffect(() => {
    if (active && focus.current) {
      focus.current = false;
      input.current.focus();
      input.current.select();
    }
  });
  if (!active) return null;
  const href = editor.getAttributes('link').href || '';
  const c = editor.view.coordsAtPos(editor.state.selection.from);
  const box = host.getBoundingClientRect();
  const style = { top: c.bottom - box.top + 6 + 'px', left: Math.max(0, c.left - box.left) + 'px' };
  return (
    <div class="link-popup" style={style}>
      <input
        ref={input}
        value={href}
        onInput={(e) => editor.chain().extendMarkRange('link').updateAttributes('link', { href: e.target.value }).run()}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), editor.commands.focus())}
      />
      <a class="ib" href={href} target="_blank" rel="noopener" title="Open link" aria-label="Open link">
        <Icon name="open" />
      </a>
      <IconButton
        icon="unlink"
        title="Remove link"
        run={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
      />
    </div>
  );
}

// A click below the last block puts the cursor in a paragraph at the end,
// adding one when the post ends with something else (a tweet, a figure …).
function clickBelow(editor, e) {
  const { view } = editor;
  const last = view.dom.lastElementChild;
  if (!last || e.clientY <= last.getBoundingClientRect().bottom || e.target.closest('.link-popup')) return;
  e.preventDefault();
  e.stopPropagation();
  const { state } = view;
  const end = state.doc.lastChild;
  let tr = state.tr;
  if (!(end.type.name === 'paragraph' && end.content.size === 0))
    tr = tr.insert(state.doc.content.size, state.schema.nodes.paragraph.create());
  tr.setSelection(TextSelection.create(tr.doc, tr.doc.content.size - 1));
  view.dispatch(tr.scrollIntoView());
  view.focus();
}

// Local time with its offset, like the posts' dates (2026-06-07T17:27:48+05:30).
function localDate(d = new Date()) {
  const pad = (n) => String(Math.abs(n)).padStart(2, '0');
  const off = -d.getTimezoneOffset();
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${off < 0 ? '-' : '+'}${pad(Math.trunc(off / 60))}:${pad(off % 60)}`
  );
}

// "Science › Space" for a category, following its parents.
function categoryPath(cats, slug) {
  const names = [];
  for (let c = cats.find((x) => x.slug === slug); c; c = cats.find((x) => x.slug === c.parent)) names.unshift(c.name);
  return names.join(' › ');
}

// A multi-select dropdown: the selected values as chips (an item's `title`,
// e.g. a category's full path, or its name); "+ <noun>" opens the
// list (a tree when items have parents), with a filter. It stays open for
// several picks. With `free`, typed text that matches no item can be added.
function MenuPicker({ items, selected, onChange, noun, free }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef();
  const filter = useRef();
  useEffect(() => {
    if (!open) return;
    filter.current?.focus();
    // only the dropdown and its "+" button count as inside; the row around them does not
    const down = (e) =>
      !e.target.closest?.('.menu, .chip.add') || !box.current?.contains(e.target) ? setOpen(false) : null;
    document.addEventListener('mousedown', down, true);
    return () => document.removeEventListener('mousedown', down, true);
  }, [open]);
  const byValue = Object.fromEntries(items.map((c) => [c.value, c]));
  const ancestors = (c) => (c.parent && byValue[c.parent] ? [c.parent, ...ancestors(byValue[c.parent])] : []);
  const depth = (c) => ancestors(c).length;
  // A filtered tree keeps the parents of the matches, for context.
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hits = items.filter((c) => words.every((w) => c.name.toLowerCase().includes(w)));
  const shown = new Set(words.length ? hits.flatMap((c) => [c.value, ...ancestors(c)]) : items.map((c) => c.value));
  const typed = q.trim();
  const canAdd = free && typed && !items.some((c) => c.value === typed) && !selected.includes(typed);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const add = () => (onChange([...selected, typed]), setQ(''));
  return (
    <div
      class="menu-picker"
      ref={box}
      onKeyDown={(e) => open && e.key === 'Escape' && ((swallowCancel = true), setOpen(false))}
    >
      <span class="chips">
        {selected.map((v) => (
          <span class="chip on">
            {byValue[v]?.title || byValue[v]?.name || v}
            <button type="button" title="Remove" onClick={() => toggle(v)}>
              ×
            </button>
          </span>
        ))}
        <button type="button" class="chip add" onClick={() => (setOpen(!open), setQ(''))}>
          + {noun}
        </button>
      </span>
      {open && (
        <div class="menu">
          <input
            type="search"
            placeholder={
              free
                ? `Filter or add a ${noun.toLowerCase()}`
                : `Filter ${noun === 'Category' ? 'categories' : noun.toLowerCase() + 's'}`
            }
            ref={filter}
            value={q}
            onInput={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const exact = items.find((c) => c.value.toLowerCase() === typed.toLowerCase());
              if (exact) toggle(exact.value);
              else if (canAdd) add();
              else if (hits[0]) toggle(hits[0].value);
            }}
          />
          <div class="menu-list" role="listbox" aria-multiselectable="true">
            {canAdd && (
              <button type="button" class="menu-row new" onClick={add}>
                <span class="menu-check">+</span>Add “{typed}”
              </button>
            )}
            {items
              .filter((c) => shown.has(c.value))
              .map((c) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={selected.includes(c.value)}
                  class={'menu-row' + (selected.includes(c.value) ? ' on' : '')}
                  style={{ paddingLeft: 10 + depth(c) * 18 + 'px' }}
                  onClick={() => toggle(c.value)}
                >
                  <span class="menu-check">{selected.includes(c.value) ? '✓' : ''}</span>
                  {c.name}
                </button>
              ))}
            {!shown.size && !canAdd && <p class="hint">No match.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// Escape in a dropdown inside a dialog closes only the dropdown.
let swallowCancel = false;

// A modal dialog with a title bar and a row of actions; actions(close) renders them.
function Dialog({ title, onClose, actions, children }) {
  const ref = useRef();
  useEffect(() => ref.current.showModal(), []);
  const close = () => ref.current.close();
  return (
    <dialog
      ref={ref}
      class="dialog"
      onClose={onClose}
      onCancel={(e) => swallowCancel && (e.preventDefault(), (swallowCancel = false))}
      onClick={(e) => e.target === ref.current && close()}
    >
      <header>
        <b>{title}</b>
        <IconButton icon="close" title="Close" run={close} />
      </header>
      <div class="dialog-body">{children}</div>
      <footer>{actions(close)}</footer>
    </dialog>
  );
}

// The post's fields that have no place on the page (Post settings dialog).
function PostFields({ post, change, slug, setSlug, creating }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    fetch('/api/meta')
      .then((r) => r.json())
      .then(setMeta);
  }, []);
  const date = String(post.date || '');
  const offset = date.slice(19) || localDate().slice(19);
  const cats = post.categories || [];
  const tags = post.tags || [];
  const catList = (meta?.categories || [])
    .map((c) => ({ ...c, path: categoryPath(meta.categories, c.slug) }))
    .sort((a, b) => a.path.localeCompare(b.path));
  return (
    <div class="post-tab form">
      <div class="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <label>
          <span>{creating ? 'Slug' : 'Slug (renamed when you save)'}</span>
          <input
            value={slug}
            onInput={(e) => (setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-')), change({}))}
          />
        </label>
        <label>
          <span>Date</span>
          <input
            type="datetime-local"
            step="1"
            value={date.slice(0, 19)}
            onInput={(e) => {
              const v = e.target.value;
              if (v) change({ date: (v.length === 16 ? v + ':00' : v) + offset });
            }}
          />
        </label>
      </div>
      <div class="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <label>
          <span>Author</span>
          <select value={post.author || ''} onChange={(e) => change({ author: e.target.value || undefined })}>
            <option value="">none</option>
            {(meta?.authors || []).map((a) => (
              <option value={a.slug}>{a.name}</option>
            ))}
          </select>
        </label>
        <label class="check">
          <input
            type="checkbox"
            checked={!!post.featured}
            onChange={(e) => change({ featured: e.target.checked || undefined })}
          />
          <span>Top Reads</span>
        </label>
      </div>
      <div class="form-row">
        <div class="field">
          <span>Categories</span>
          <MenuPicker
            items={catList.map((c) => ({ value: c.slug, name: c.name, title: c.path, parent: c.parent }))}
            selected={cats}
            onChange={(categories) => change({ categories })}
            noun="Category"
          />
        </div>
      </div>
      <div class="form-row">
        <div class="field">
          <span>Tags</span>
          <MenuPicker
            items={(meta?.tags || []).map((t) => ({ value: t, name: t }))}
            selected={tags}
            onChange={(t) => change({ tags: t })}
            noun="Tag"
            free
          />
        </div>
      </div>
      <div class="form-row">
        <label>
          <span>Excerpt</span>
          <textarea
            class="code excerpt"
            rows={2}
            placeholder="Empty: the site cuts one from the post text"
            value={post.excerpt || ''}
            onInput={(e) => change({ excerpt: e.target.value || undefined })}
          />
        </label>
      </div>
      {!creating && (
        <div class="hint">
          Modified{' '}
          {String(post.modified || '')
            .replace('T', ' ')
            .slice(0, 19)}{' '}
          (set when you save)
        </div>
      )}
    </div>
  );
}

let dirty = false;

function PostEditor({ slug }) {
  const ref = useRef();
  const titleRef = useRef();
  const hostRef = useRef();
  const focusLink = useRef(false);
  const [editor, setEditor] = useState(null);
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('');
  const [, tick] = useReducer((x) => x + 1, 0);
  const [captionEditor, setCaptionEditor] = useState(null);
  const [showMeta, setShowMeta] = useState(false);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    fetch(`/api/posts/${slug}/draft`)
      .then((r) => r.json())
      .then((d) => setDraft(d.draft));
  }, [slug]);
  // Publish saves first; both add or remove the post's .draft file.
  const setDraftState = async (on) => {
    if (!on && status === 'unsaved' && !(await save.current())) return;
    const r = await fetch(`/api/posts/${slug}/draft`, { method: on ? 'PUT' : 'DELETE' });
    if (r.ok) setDraft((await r.json()).draft);
  };
  const save = useRef();
  // Change post fields (not the body); undefined removes a field.
  const change = (fields) => {
    setPost((p) => ({ ...p, ...fields }));
    setStatus('unsaved');
  };

  useEffect(() => {
    let ed;
    let alive = true;
    setMediaSlug(slug);
    fetch(`/api/posts/${slug}`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        setPost(data);
        ed = new Editor({
          element: ref.current,
          extensions: [...extensions, UndoRedo, Dropcursor, Gapcursor],
          content: captionsForEditing(data.body),
          onTransaction: tick,
          onUpdate: () => setStatus('unsaved'),
        });
        setEditor(ed);
        setStatus('');
      });
    return () => {
      alive = false;
      ed?.destroy();
    };
  }, [slug]);

  // The media dialog warns before deleting a file the post uses.
  setMediaInUse(
    (name) =>
      post?.featured_image === name || (!!editor && JSON.stringify(editor.getJSON()).includes(JSON.stringify(name)))
  );

  // The title wraps and grows instead of scrolling.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [post?.title]);

  save.current = async () => {
    if (!editor) return;
    const body = captionsForSaving(clean(editor.getJSON(), editor.schema));
    const next = status === 'unsaved' ? { ...post, modified: localDate() } : post;
    setStatus('saving…');
    const r = await fetch(`/api/posts/${slug}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...next, body }),
    });
    if (!r.ok) return (setStatus(`save failed: ${r.status}`), false);
    setPost(next);
    setStatus('saved');
    dirty = false;
    return true;
  };
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        save.current();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    dirty = status === 'unsaved';
    const onLeave = (e) => dirty && e.preventDefault();
    addEventListener('beforeunload', onLeave);
    return () => removeEventListener('beforeunload', onLeave);
  }, [status]);

  return (
    <>
      <div class="bar">
        <a class="back" href="/" onClick={(e) => (e.preventDefault(), leave() && navigate('/'))}>
          ← <span class="back-text">All posts</span>
        </a>
        {editor && (
          <Toolbar editor={editor} captionEditor={captionEditor} onLink={() => ((focusLink.current = true), tick())} />
        )}
        <span class="bar-end">
          <span class="status">{status}</span>
          {draft !== null && <span class={'badge' + (draft ? ' draft' : '')}>{draft ? 'Draft' : 'Published'}</span>}
          <IconButton icon="settings" title="Post settings" run={() => setShowMeta(true)} />
          {draft === false && <IconButton icon="unpublish" title="Move to drafts" run={() => setDraftState(true)} />}
          <button class={draft ? 'secondary' : 'save'} onClick={() => save.current()}>
            Save
          </button>
          {draft && (
            <button class="save" onClick={() => setDraftState(false)}>
              Publish
            </button>
          )}
        </span>
      </div>
      {showMeta && (
        <SettingsDialog
          slug={slug}
          onClose={() => setShowMeta(false)}
          beforeRename={() => (dirty ? save.current() : true)}
          onSaved={(meta, to) => {
            setPost((p) => ({ ...p, ...meta }));
            if (to !== slug) navigate(`/p/${to}`, true);
          }}
        />
      )}
      <article>
        {post && (
          <textarea
            ref={titleRef}
            class="title"
            rows={1}
            value={post.title}
            onInput={(e) => change({ title: e.target.value })}
          />
        )}
        {post && <FeaturedImage post={post} change={change} onCaptionEditor={(e) => (setCaptionEditor(e), tick())} />}
        <div class="body" ref={hostRef} onMouseDownCapture={(e) => editor && clickBelow(editor, e)}>
          <div ref={ref} />
          {editor && <LinkPopup editor={editor} host={hostRef.current} focus={focusLink} />}
        </div>
      </article>
    </>
  );
}

// Leaving the open post: ask first when it has unsaved changes.
function leave() {
  if (dirty && !confirm('Discard unsaved changes?')) return false;
  dirty = false;
  return true;
}

const slugify = (t) =>
  t
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// The fields the settings dialog owns.
const META = ['date', 'author', 'categories', 'tags', 'featured', 'excerpt'];

// Post settings, from the post list or the editor, saved on their own: Save
// applies these fields to the post file as it is on disk now (the body and
// title are left alone) and renames the directory when the slug changed.
// beforeRename() runs first when the slug changes (the editor saves itself).
// Without a slug it creates a post instead: a title field leads, the slug
// follows the title until it is edited, and Create opens the new draft.
function SettingsDialog({ slug, onClose, onSaved, beforeRename, onCreated }) {
  const creating = !slug;
  const [post, setPost] = useState(creating ? { title: '', date: localDate(), categories: [], tags: [] } : null);
  const [newSlug, setNewSlug] = useState(slug || '');
  const [slugEdited, setSlugEdited] = useState(false);
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (creating) return;
    fetch(`/api/posts/${slug}`)
      .then((r) => r.json())
      .then(setPost);
  }, [slug]);
  const change = (fields) => (setPost((p) => ({ ...p, ...fields })), setChanged(true));
  const create = async (close) => {
    const meta = Object.fromEntries(META.map((k) => [k, post[k]]));
    const r = await fetch('/api/posts', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ title: post.title, slug: newSlug.replace(/^-+|-+$/g, ''), ...meta }),
    });
    if (!r.ok) return setError(await r.text());
    const { slug: made } = await r.json();
    close();
    onCreated(made);
  };
  const save = async (close) => {
    const to = (newSlug || slug).replace(/^-+|-+$/g, '');
    if (to !== slug && beforeRename && !(await beforeRename())) return setError('Saving the post failed.');
    const disk = await (await fetch(`/api/posts/${slug}`)).json();
    const meta = Object.fromEntries(META.map((k) => [k, post[k]]));
    meta.modified = localDate();
    const r = await fetch(`/api/posts/${slug}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...disk, ...meta }),
    });
    if (!r.ok) return setError(`Save failed: ${r.status}`);
    if (to !== slug) {
      const m = await fetch(`/api/posts/${slug}/rename`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ to }),
      });
      if (!m.ok) return setError(`Saved, but not renamed: ${await m.text()}`);
    }
    onSaved(meta, to);
    close();
  };
  if (!post) return null;
  return (
    <Dialog
      title={creating ? 'New post' : post.title}
      onClose={onClose}
      actions={(close) => (
        <>
          {error && <span class="error">{error}</span>}
          <button class="secondary" onClick={close}>
            Cancel
          </button>
          {creating ? (
            <button class="primary" disabled={!post.title.trim() || !newSlug} onClick={() => create(close)}>
              Create draft
            </button>
          ) : (
            <button class="primary" disabled={!changed} onClick={() => save(close)}>
              Save
            </button>
          )}
        </>
      )}
    >
      {creating && (
        <div class="form title-field">
          <label>
            <span>Title</span>
            <input
              autoFocus
              value={post.title}
              onInput={(e) => {
                change({ title: e.target.value });
                if (!slugEdited) setNewSlug(slugify(e.target.value));
              }}
            />
          </label>
        </div>
      )}
      <PostFields
        post={post}
        change={change}
        slug={newSlug}
        setSlug={(v) => (setSlugEdited(true), setNewSlug(v))}
        creating={creating}
      />
    </Dialog>
  );
}

// The start screen: find a post to edit, or create one.
function Start() {
  const [posts, setPosts] = useState(null);
  const [query, setQuery] = useState('');
  const [settings, setSettings] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () =>
    fetch('/api/posts')
      .then((r) => r.json())
      .then(setPosts);
  useEffect(() => void load(), []);
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const [which, setWhich] = useState('all');
  const inView = (p) => which === 'all' || (which === 'drafts') === !!p.draft;
  const shown = (posts || []).filter(
    (p) => inView(p) && words.every((w) => (p.title + ' ' + p.slug).toLowerCase().includes(w))
  );
  const count = (k) => (posts || []).filter((p) => k === 'all' || (k === 'drafts') === !!p.draft).length;
  return (
    <main class="start">
      <TopNav at="posts" />
      <div class="start-bar">
        <input
          class="search"
          type="search"
          placeholder="Search posts by title"
          autoFocus
          value={query}
          onInput={(e) => setQuery(e.target.value)}
        />
        <button class="primary" onClick={() => setCreating(true)}>
          New post
        </button>
      </div>
      {creating && <SettingsDialog onClose={() => setCreating(false)} onCreated={(made) => navigate(`/p/${made}`)} />}
      {settings && <SettingsDialog slug={settings} onClose={() => setSettings(null)} onSaved={load} />}
      <div class="tabs list-tabs" role="tablist">
        {[
          ['all', 'All'],
          ['published', 'Published'],
          ['drafts', 'Drafts'],
        ].map(([k, l]) => (
          <button
            role="tab"
            aria-selected={which === k}
            class={'tab' + (which === k ? ' on' : '')}
            onClick={() => setWhich(k)}
          >
            {l} <span class="count">{posts ? count(k) : ''}</span>
          </button>
        ))}
      </div>
      <p class="hint">{posts ? `${shown.length} shown` : 'Loading…'}</p>
      <div class="post-grid">
        {shown.map((p) => (
          <a class="card" href={'/p/' + p.slug} onClick={(e) => (e.preventDefault(), navigate('/p/' + p.slug))}>
            <IconButton class="card-settings" icon="settings" title="Post settings" run={() => setSettings(p.slug)} />
            <div class="card-pic">
              {p.featured_image && (
                <img
                  src={
                    /^(https?:)?\/\//.test(p.featured_image)
                      ? p.featured_image
                      : `/media/${p.slug}/${encodeURIComponent(p.featured_image)}`
                  }
                  alt=""
                  loading="lazy"
                  referrerpolicy="no-referrer"
                />
              )}
            </div>
            <div class="card-text">
              <span class="card-date">
                {p.draft && <span class="badge draft">Draft</span>} {String(p.date).slice(0, 10)}
              </span>
              <span class="card-title">{p.title}</span>
              <span class="card-excerpt">{p.excerpt}</span>
            </div>
          </a>
        ))}
      </div>
    </main>
  );
}

// Posts | Authors, at the top of the two list screens.
function TopNav({ at }) {
  const link = (to, k, l) => (
    <a href={to} class={'top-link' + (at === k ? ' on' : '')} onClick={(e) => (e.preventDefault(), navigate(to))}>
      {l}
    </a>
  );
  return (
    <nav class="top-nav">
      {link('/', 'posts', 'Posts')}
      {link('/authors', 'authors', 'Authors')}
    </nav>
  );
}

const authorFile = (slug, f) => `/author-media/${slug}/${encodeURIComponent(f)}`;

function Avatar({ slug, author, size = 56 }) {
  return author.avatar ? (
    <img class="avatar" src={authorFile(slug, author.avatar)} alt="" style={{ width: size, height: size }} />
  ) : (
    <span class="avatar initials" style={{ width: size, height: size, fontSize: size / 2.6 }}>
      {(author.name || '?')
        .split(/\s+/)
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()}
    </span>
  );
}

// An author's name, nickname, description and avatar, saved on their own.
// Deleting is offered only for an author without posts.
// Without a slug it creates an author; an avatar picked then is uploaded
// right after the author's directory exists.
function AuthorDialog({ slug, posts = 0, onClose, onSaved }) {
  const creating = !slug;
  const [author, setAuthor] = useState(creating ? { name: '', nickname: '', description: '', avatar: null } : null);
  const [pending, setPending] = useState(null); // { file, url } of an avatar picked before creating
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (creating) return;
    fetch(`/api/authors/${slug}`)
      .then((r) => r.json())
      .then(setAuthor);
  }, [slug]);
  const set = (f) => (setAuthor((a) => ({ ...a, ...f })), setChanged(true));
  const create = async (close) => {
    const r = await fetch('/api/authors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(author),
    });
    if (!r.ok) return setError(await r.text());
    const { slug: made } = await r.json();
    if (pending) {
      const u = await fetch(`/api/authors/${made}/media/${encodeURIComponent(pending.file.name)}`, {
        method: 'PUT',
        body: pending.file,
      });
      if (u.ok) {
        const { name } = await u.json();
        const saved = await (await fetch(`/api/authors/${made}`)).json();
        await fetch(`/api/authors/${made}`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ...saved, avatar: name }),
        });
      }
    }
    onSaved();
    close();
  };
  const save = async (close) => {
    const r = await fetch(`/api/authors/${slug}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(author),
    });
    if (!r.ok) return setError(`Save failed: ${r.status}`);
    onSaved();
    close();
  };
  const remove = async (close) => {
    if (!confirm(`Delete the author "${author.name}" and their avatar?`)) return;
    const r = await fetch(`/api/authors/${slug}`, { method: 'DELETE' });
    if (!r.ok) return setError(await r.text());
    onSaved();
    close();
  };
  if (!author) return null;
  return (
    <Dialog
      title={creating ? 'New author' : author.name || slug}
      onClose={onClose}
      actions={(close) => (
        <>
          {!creating && posts === 0 && (
            <button class="secondary danger" onClick={() => remove(close)}>
              Delete author
            </button>
          )}
          {error && <span class="error">{error}</span>}
          <span class="spacer" />
          <button class="secondary" onClick={close}>
            Cancel
          </button>
          {creating ? (
            <button class="primary" disabled={!author.name.trim()} onClick={() => create(close)}>
              Create
            </button>
          ) : (
            <button class="primary" disabled={!changed} onClick={() => save(close)}>
              Save
            </button>
          )}
        </>
      )}
    >
      <div class="author-form">
        {creating ? (
          <div class="author-pic">
            {pending ? (
              <img class="avatar" src={pending.url} alt="" style={{ width: 96, height: 96 }} />
            ) : (
              <Avatar author={author} size={96} />
            )}
            <label class="pick-btn">
              <Icon name="image" /> {pending ? 'Change avatar' : 'Add avatar'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setPending({ file, url: URL.createObjectURL(file) });
                }}
              />
            </label>
            {pending && (
              <button class="link-btn" onClick={() => setPending(null)}>
                Remove avatar
              </button>
            )}
          </div>
        ) : (
          <div class="author-pic">
            <Avatar slug={slug} author={author} size={96} />
            <MediaPicker
              kind="image"
              value={author.avatar}
              onPick={(avatar) => set({ avatar })}
              api={`/api/authors/${slug}`}
              fileUrl={(f) => authorFile(slug, f)}
              inUse={(f) => author.avatar === f}
              autoOpen={false}
              label={author.avatar ? 'Change avatar' : 'Add avatar'}
            />
            {author.avatar && (
              <button class="link-btn" onClick={() => set({ avatar: null })}>
                Remove avatar
              </button>
            )}
          </div>
        )}
        <div class="form">
          <div class="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <label>
              <span>Name</span>
              <input autoFocus={creating} value={author.name} onInput={(e) => set({ name: e.target.value })} />
            </label>
            <label>
              <span>Nickname</span>
              <input value={author.nickname || ''} onInput={(e) => set({ nickname: e.target.value })} />
            </label>
          </div>
          <label>
            <span>Description</span>
            <textarea
              class="code excerpt"
              rows={4}
              value={author.description || ''}
              onInput={(e) => set({ description: e.target.value })}
            />
          </label>
          <p class="hint">
            {creating
              ? 'The slug is made from the name.'
              : posts === 0
                ? 'No posts yet, so this author can be deleted.'
                : `${posts} post${posts === 1 ? '' : 's'} by this author.`}
          </p>
        </div>
      </div>
    </Dialog>
  );
}

// The authors screen: every author as a card; a card opens its dialog.
function Authors() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () =>
    fetch('/api/authors')
      .then((r) => r.json())
      .then(setList);
  useEffect(() => void load(), []);
  const current = list?.find((a) => a.slug === open);
  return (
    <main class="start">
      <TopNav at="authors" />
      <div class="start-bar">
        <span class="hint grow">{list ? `${list.length} authors` : 'Loading…'}</span>
        <button class="primary" onClick={() => setCreating(true)}>
          New author
        </button>
      </div>
      {creating && <AuthorDialog onClose={() => setCreating(false)} onSaved={load} />}
      <div class="author-grid">
        {(list || []).map((a) => (
          <button class="author-card" onClick={() => setOpen(a.slug)}>
            <Avatar slug={a.slug} author={a} />
            <span class="author-text">
              <span class="card-title">{a.name}</span>
              <span class="card-date">
                {a.posts} post{a.posts === 1 ? '' : 's'}
              </span>
              <span class="card-excerpt">{a.description}</span>
            </span>
          </button>
        ))}
      </div>
      {current && (
        <AuthorDialog slug={current.slug} posts={current.posts} onClose={() => setOpen(null)} onSaved={load} />
      )}
    </main>
  );
}

// Routes: / is the post list, /p/<slug> a post's editor.
const slugFromPath = () => {
  const m = /^\/p\/([^/]+)\/?$/.exec(location.pathname);
  return m ? decodeURIComponent(m[1]) : '';
};
function navigate(path, replace = false) {
  history[replace ? 'replaceState' : 'pushState'](null, '', path);
  dispatchEvent(new Event('navigate'));
}

function App() {
  const [slug, setSlug] = useState(slugFromPath());
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const onNav = () => (setSlug(slugFromPath()), setPath(location.pathname));
    // The browser's back/forward: ask first when the open post has unsaved changes.
    const onPop = () => {
      if (dirty && !confirm('Discard unsaved changes?')) return history.pushState(null, '', '/p/' + slug);
      dirty = false;
      onNav();
    };
    addEventListener('navigate', onNav);
    addEventListener('popstate', onPop);
    return () => {
      removeEventListener('navigate', onNav);
      removeEventListener('popstate', onPop);
    };
  }, [slug]);
  if (slug) return <PostEditor key={slug} slug={slug} />;
  return path === '/authors' ? <Authors /> : <Start />;
}

render(<App />, document.getElementById('app'));
